// UnternehmenMehrfachWahl.tsx — „Unternehmen wählen" als Mehrfachauswahl in
// Dialogen (Weiterer Kommunikationsweg, Kontaktdaten verwalten). User
// 02.10.2026: Dropdown mit Häkchen und „Fertig"-Knopf.
//
// Die Auswahl gilt sofort (jedes Häkchen ruft `onChange`); „Fertig" schließt
// nur. Steht genau EINE aktive Firma zur Wahl, ist sie immer gesetzt — ihr
// Häkchen ist gesperrt. Eine gespeicherte Firma, die nicht (mehr) wählbar ist
// (ausgeschieden), steht unten mit in der Liste, damit man sie abwählen kann.
//
// Das Menü liegt im Portal an <body>, weil die Dialoge `overflow: hidden`
// haben. React leitet Klicks im Portal trotzdem an die Vorfahren im
// Komponentenbaum weiter — der Dialog fängt sie per stopPropagation ab, ein
// Klick ins Menü schließt ihn also nicht.

import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { firmenAnzeige } from "../../../utils/firmenAnzeige";
import { gleicheFirma, type FirmaOption } from "../../../utils/geschaeftlichFirma";
import { alleDokumente, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

/** Alle Einträge des Menüs: die wählbaren Firmen, dahinter gewählte, die nicht (mehr) wählbar sind. */
export function firmenOptionen(firmen: readonly FirmaOption[], gewaehlt: readonly FirmaOption[]): FirmaOption[] {
  const out = [...firmen];
  for (const g of gewaehlt) if (!out.some((f) => gleicheFirma(f, g))) out.push(g);
  return out;
}

/** Eine Firma an- bzw. abwählen; die Reihenfolge der Auswahl bleibt (die erste ist die Hauptfirma). */
export function firmaUmschalten(gewaehlt: readonly FirmaOption[], firma: FirmaOption): FirmaOption[] {
  return gewaehlt.some((g) => gleicheFirma(g, firma))
    ? gewaehlt.filter((g) => !gleicheFirma(g, firma))
    : [...gewaehlt, firma];
}

/** Die immer gesetzte Firma: genau eine wählbare. */
export const gesperrteFirma = (firmen: readonly FirmaOption[]): FirmaOption | null =>
  firmen.length === 1 ? firmen[0] : null;

const useStyles = makeStyles({
  ausloeser: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
    padding: "10px 14px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    color: REFINED.ink,
    textAlign: "left",
    backgroundColor: REFINED.fieldBg,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    cursor: "pointer",
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: "2px",
      outlineColor: REFINED.aub700,
      outlineOffset: "1px",
    },
  },
  ausloeserText: {
    whiteSpace: "nowrap",
    ...shorthands.overflow("hidden"),
    textOverflow: "ellipsis",
  },
  platzhalter: { color: REFINED.ink3 },
  pfeil: { color: REFINED.ink3, flexShrink: 0, fontSize: "0.75rem" },
  menu: {
    position: "fixed",
    zIndex: 100010,
    boxSizing: "border-box",
    backgroundColor: "#fff",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 8px 24px rgba(58,12,49,.16), 0 2px 6px rgba(58,12,49,.08)",
    ...shorthands.padding("6px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    fontFamily: REFINED.fontUI,
  },
  liste: {
    maxHeight: "240px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  eintrag: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    ...shorthands.padding("7px", "10px"),
    ...shorthands.borderRadius("7px"),
    fontSize: "0.88rem",
    color: REFINED.ink,
    cursor: "pointer",
    ":hover": { backgroundColor: "rgba(125, 32, 105, 0.07)" },
    "& input": { accentColor: REFINED.aub, width: "16px", height: "16px", cursor: "pointer", flexShrink: 0 },
  },
  eintragGesperrt: { cursor: "default", "& input": { cursor: "default" } },
  eintragName: { whiteSpace: "nowrap", ...shorthands.overflow("hidden"), textOverflow: "ellipsis" },
  hinweis: { color: REFINED.ink3, fontSize: "0.78rem", marginLeft: "auto", flexShrink: 0 },
  fuss: {
    display: "flex",
    justifyContent: "flex-end",
    ...shorthands.padding("6px", "4px", "2px"),
    borderTop: `1px solid ${REFINED.line}`,
    marginTop: "4px",
  },
  fertig: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.82rem",
    padding: "5px 14px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub700 },
  },
});

export interface UnternehmenMehrfachWahlProps {
  /** Wählbare (aktive) Firmen. */
  firmen: FirmaOption[];
  gewaehlt: FirmaOption[];
  onChange: (gewaehlt: FirmaOption[]) => void;
  /** Für <label htmlFor>. */
  id?: string;
  /** Text ohne Auswahl. */
  platzhalter?: string;
}

export function UnternehmenMehrfachWahl({
  firmen,
  gewaehlt,
  onChange,
  id,
  platzhalter = "Unternehmen wählen",
}: UnternehmenMehrfachWahlProps) {
  const s = useStyles();
  const ankerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [offen, setOffen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number } | null>(null);

  const schliessen = React.useCallback((fokusZurueck = false) => {
    setOffen(false);
    if (fokusZurueck) ankerRef.current?.focus();
  }, []);

  React.useLayoutEffect(() => {
    if (!offen) {
      setPos(null);
      return;
    }
    const r = ankerRef.current?.getBoundingClientRect();
    if (!r) return;
    const hoehe = menuRef.current?.offsetHeight ?? 0;
    const width = Math.max(220, r.width);
    const left = Math.max(8, Math.min(r.left, aktivesFenster().innerWidth - width - 8));
    const unten = r.bottom + 4;
    const top = hoehe && unten + hoehe > aktivesFenster().innerHeight - 8 ? Math.max(8, r.top - hoehe - 4) : unten;
    setPos({ top, left, width });
  }, [offen]);

  React.useEffect(() => {
    if (!offen) return;
    const weg = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || ankerRef.current?.contains(t)) return;
      schliessen();
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Nur das Menü schließen, nicht den Dialog dahinter.
        e.stopPropagation();
        schliessen(true);
      }
    };
    alleDokumente.addEventListener("mousedown", weg, true);
    alleDokumente.addEventListener("keydown", taste, true);
    menuRef.current?.querySelector<HTMLElement>("input:not(:disabled)")?.focus();
    return () => {
      alleDokumente.removeEventListener("mousedown", weg, true);
      alleDokumente.removeEventListener("keydown", taste, true);
    };
  }, [offen, schliessen]);

  const gesperrt = gesperrteFirma(firmen);
  const optionen = firmenOptionen(firmen, gewaehlt);
  const { text, voll } = firmenAnzeige(gewaehlt.map((f) => f.name), 48);

  return (
    <>
      <button
        ref={ankerRef}
        id={id}
        type="button"
        className={s.ausloeser}
        aria-haspopup="true"
        aria-expanded={offen}
        title={voll || undefined}
        onClick={() => setOffen((o) => !o)}
      >
        <span className={mergeClasses(s.ausloeserText, !text && s.platzhalter)}>{text || platzhalter}</span>
        <span className={s.pfeil} aria-hidden="true">
          ▾
        </span>
      </button>
      {offen &&
        createPortal(
          <div
            ref={menuRef}
            role="group"
            aria-label="Unternehmen wählen"
            className={s.menu}
            style={pos ? { top: pos.top, left: pos.left, width: pos.width } : { top: -9999, left: -9999 }}
          >
            <div className={s.liste}>
              {optionen.map((f) => {
                const an = gewaehlt.some((g) => gleicheFirma(g, f));
                const fest = !!gesperrt && gleicheFirma(gesperrt, f);
                const waehlbar = firmen.some((w) => gleicheFirma(w, f));
                return (
                  <label key={`${f.id ?? ""}-${f.name}`} className={mergeClasses(s.eintrag, fest && s.eintragGesperrt)} title={f.name}>
                    <input
                      type="checkbox"
                      checked={an || fest}
                      disabled={fest}
                      onChange={() => onChange(firmaUmschalten(gewaehlt, f))}
                    />
                    <span className={s.eintragName}>{f.name}</span>
                    {!waehlbar && <span className={s.hinweis}>nicht mehr verbunden</span>}
                  </label>
                );
              })}
            </div>
            <div className={s.fuss}>
              <button type="button" className={s.fertig} onClick={() => schliessen(true)}>
                Fertig
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default UnternehmenMehrfachWahl;
